import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useDiagnosticsContext } from '../hooks/useDiagnosticsContext';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
} from '@mui/icons-material';
import { Job, UpdateJobDto, JobType, Priority, DuplicateJobDto } from '../services/jobService';
import { Customer } from '../services/customerService';
import { Business } from '../services/businessService';
import { Appointment } from '../services/appointmentService';
import { AppointmentType } from '../services/appointmentTypeService';
import { formService, Form, FormFieldType } from '../services/formService';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import { offlineService } from '../services/offlineService';
import {
  documentService,
  JobDocument,
  UploadConstraints,
  FormPdfFreshness
} from '../services/documentService';
import CustomerDetailsCard from '../components/common/CustomerDetailsCard';
import JobDetailsCard from '../components/jobDetail/JobDetailsCard';
import AppointmentsList from '../components/jobDetail/AppointmentsList';
import BaseDialog from '../components/common/BaseDialog';
import JobForm from '../components/common/JobForm';
import DuplicateJobDialog from '../components/jobDetail/DuplicateJobDialog';
import JobDocumentsTab from '../components/jobDetail/JobDocumentsTab';
import FormRenderer from '../components/forms/FormRenderer';
import TabPanel from '../components/common/TabPanel';
import UnsavedChangesDialog from '../components/common/UnsavedChangesDialog';
import PixelVersionPDFViewer from '../components/jobDetail/PixelVersionPDFViewer';
import { isPixelVersionForm, generateAndStoreFormPdf, generateFormPdf, handleSaveFormDataOnly } from '../services/pixelVersionPdfService';
import {
  useJob,
  useCustomer,
  useAppointmentsByJob,
  useAppointmentTypes,
  useBusinesses,
  useForms,
  useJobFormResponses,
  useUpdateJob,
  useDeleteJob,
  useDuplicateJob,
  useCommunicationLogsByJob,
  useCreateCommunicationLog,
} from '../hooks/queries';
import CommunicationLogsList from '../components/communications/CommunicationLogsList';
import CommunicationLogForm from '../components/communications/CommunicationLogForm';
import { CreateCommunicationLogDto } from '../services/communicationLogService';
import { useBusinessUsers } from '../hooks/useBusiness';
import { sortUsers } from '../utils/userHelpers';
import { extractApiErrorMessage } from '../utils/apiUtils';
import { debugLog } from '../utils/debugLog';
import { UserDataFieldDto } from '../types/customUserDataFields';

// State maps that need to distinguish between instances of the same multi-instance form
// are keyed by `${formId}:${instanceIndex}` so #1 and #2 don't share PDF/response state.
const formKey = (formId: number, instanceIndex: number = 1): string => `${formId}:${instanceIndex}`;

// One rendered form tab. Multi-instance forms expand to one slot per active instance;
// 1:1 forms get a single slot at instanceIndex 1.
interface FormTabSlot {
  form: Form;
  instanceIndex: number;
  label: string | null;
}

interface JobDetailPageProps {
  jobId?: string; // Optional prop for embedded usage
  embedded?: boolean; // Whether this component is embedded in another page
  customerAppointments?: Appointment[]; // Appointments passed from parent when embedded
  initialTab?: number; // Initial tab to select when embedded
  customerId?: string; // Customer ID for URL construction when embedded
  // Shared data from parent to prevent duplicate API calls
  parentAppointmentTypes?: AppointmentType[]; // Appointment types passed from parent when embedded
  parentBusiness?: Business | null; // Business data passed from parent when embedded
}

const JobDetailPage: React.FC<JobDetailPageProps> = ({ jobId, embedded = false, customerAppointments, initialTab = 0, customerId, parentAppointmentTypes, parentBusiness }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = jobId || paramId; // Use prop if provided, otherwise use params
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useBusinessTheme();

  // Debug: track parent re-renders
  const parentRenderCountRef = useRef(0);
  parentRenderCountRef.current++;

  // TanStack Query mutations
  const updateJobMutation = useUpdateJob();
  const deleteJobMutation = useDeleteJob();
  const duplicateJobMutation = useDuplicateJob();

  // TanStack Query data fetching
  const {
    data: job,
    isLoading: jobLoading,
    isFetching: jobFetching,
    isStale: jobStale,
    error: jobError,
    refetch: refetchJob
  } = useJob(id);

  const {
    data: customerData,
    isFetching: customerFetching,
    isStale: customerStale
  } = useCustomer(job?.customerId);
  const customer = customerData ?? null;

  useDiagnosticsContext(
    'job',
    job
      ? `Job ${job.jobNumber} — ${job.customerName}${job.jobAddress ? ` @ ${job.jobAddress}` : ''}`
      : null,
  );

  // For appointments: use parent data if embedded, otherwise fetch via query
  const {
    data: appointmentsFromQuery,
    isLoading: appointmentsQueryLoading,
    isFetching: appointmentsFetching,
    isStale: appointmentsStale
  } = useAppointmentsByJob(embedded ? undefined : id); // Disable query when embedded

  // Compute appointments: filter from parent if embedded, otherwise use query result
  const appointments = useMemo(() => {
    if (embedded && customerAppointments && id) {
      return customerAppointments.filter(appointment => appointment.jobId === id);
    }
    return appointmentsFromQuery || [];
  }, [embedded, customerAppointments, id, appointmentsFromQuery]);

  // For appointmentTypes: use parent data if provided, otherwise fetch
  const { data: appointmentTypesFromQuery } = useAppointmentTypes({
    businessId: user?.businessId ?? undefined,
    enabled: !parentAppointmentTypes || parentAppointmentTypes.length === 0
  });
  const appointmentTypes = parentAppointmentTypes && parentAppointmentTypes.length > 0
    ? parentAppointmentTypes
    : (appointmentTypesFromQuery || []);

  // For business: use parent data if provided, otherwise fetch
  const { data: businessesFromQuery } = useBusinesses(!parentBusiness);
  const business = parentBusiness ?? (businessesFromQuery?.[0] ?? null);

  // Business users for sales person dropdown
  const { data: businessUsersData } = useBusinessUsers({ businessId: user?.businessId ?? undefined });
  const businessUsers = useMemo(() => sortUsers(businessUsersData || []), [businessUsersData]);

  // Forms
  const { data: formsData, isLoading: formsLoading } = useForms();
  const forms = formsData || [];

  // Job form responses
  const { data: jobFormResponsesData, isLoading: formResponsesLoading, refetch: refetchFormResponses } = useJobFormResponses(id);
  // Stable reference (react-query structural sharing keeps it stable when content is unchanged)
  // so derived memos/effects below recompute only when responses actually change.
  const allJobFormResponses = useMemo(() => jobFormResponsesData || [], [jobFormResponsesData]);

  // Derived tab slots: expands multi-instance forms into one slot per active instance.
  // 1:1 forms get a single 'form' slot. Order preserved by form.displayOrder, then instanceIndex.
  // Adding a copy is driven by the "+ New Form" button on the form toolbar, not a tab.
  const tabSlots: FormTabSlot[] = useMemo(() => {
    const slots: FormTabSlot[] = [];
    for (const form of forms) {
      if (form.allowMultipleInstances) {
        const instances = allJobFormResponses
          .filter(r => r.formId === form.id && r.isActive)
          .sort((a, b) => (a.instanceIndex ?? 1) - (b.instanceIndex ?? 1));

        if (instances.length === 0) {
          // No instances yet: emit a single implied #1 slot so the user can fill in
          // the first copy without adding one first. Save will upsert at index 1.
          slots.push({ form, instanceIndex: 1, label: null });
        } else {
          for (const inst of instances) {
            slots.push({
              form,
              instanceIndex: inst.instanceIndex ?? 1,
              label: inst.label,
            });
          }
        }
      } else {
        slots.push({ form, instanceIndex: 1, label: null });
      }
    }
    return slots;
  }, [forms, allJobFormResponses]);

  // Communication logs
  const {
    data: communicationLogsData,
    isLoading: communicationLogsLoading
  } = useCommunicationLogsByJob(id);
  const createCommunicationLogMutation = useCreateCommunicationLog();

  // Local state for form responses (transformed for rendering)
  const [formResponses, setFormResponses] = useState<{ [key: string]: any }>({});
  // Start true so the Documents tab shows a spinner on first render
  // rather than an empty area while the initial fetch is in flight.
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  // Monotonic call id: each new load bumps this, and any in-flight call whose
  // id no longer matches aborts its state writes. Prevents superseded calls
  // (e.g. from StrictMode double-mount) from committing stale loading=false
  // between the winning call's docs-then-loading updates.
  const loadDocumentsCallIdRef = useRef(0);
  const loadingAppointments = appointmentsQueryLoading;
  const loadingForms = formsLoading;
  const loadingFormResponses = formResponsesLoading;
  const loading = jobLoading;
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState<number | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Use ref to track dirty state for navigation blocking (avoids stale closure issues)
  const isFormDirtyRef = useRef(false);
  useEffect(() => {
    isFormDirtyRef.current = isFormDirty;
  }, [isFormDirty]);

  // Log when form dirty state changes
  useEffect(() => {
    debugLog('[JobDetailPage] isFormDirty changed to:', isFormDirty);
  }, [isFormDirty]);

  // Warn on browser refresh/close when there are unsaved form changes
  useUnsavedChanges({ isDirty: isFormDirty });

  // Block navigation when form is dirty
  useEffect(() => {
    if (!isFormDirty) return;

    // Block browser back/forward buttons
    const handlePopState = (e: PopStateEvent) => {
      if (isFormDirtyRef.current) {
        debugLog('[JobDetailPage] Blocking popstate navigation - form is dirty');
        // Push current state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
        setPendingNavigation('back');
        setShowUnsavedChangesDialog(true);
      }
    };

    // Push initial state so we can detect back button
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isFormDirty]);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateAddress, setDuplicateAddress] = useState('');
  const [tabValue, setTabValue] = useState(initialTab);
  const [isOffline, setIsOffline] = useState(!offlineService.getOnlineStatus());

  // Document state
  const [uploadedDocuments, setUploadedDocuments] = useState<JobDocument[]>([]);
  const [uploadConstraints, setUploadConstraints] = useState<UploadConstraints | null>(null);

  // Batch upload state
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [batchSelectedFiles, setBatchSelectedFiles] = useState<any[]>([]);

  // PDF state for PixelVersion forms
  const [pixelVersionPdfBytes, setPixelVersionPdfBytes] = useState<{ [key: string]: Uint8Array }>({});
  const [loadingPixelVersionPdfs, setLoadingPixelVersionPdfs] = useState<{ [key: string]: boolean }>({});

  // Form PDF freshness state for lazy PDF generation
  const [formPdfFreshness, setFormPdfFreshness] = useState<FormPdfFreshness[]>([]);
  const [checkingFreshness, setCheckingFreshness] = useState(false);
  const [regeneratingFormIds, setRegeneratingFormIds] = useState<Set<string>>(new Set());

  // Multi-instance form UX: add / rename / delete dialogs
  const [addInstanceForm, setAddInstanceForm] = useState<Form | null>(null);
  const [addInstanceLabel, setAddInstanceLabel] = useState('');
  const [renameInstance, setRenameInstance] = useState<{ form: Form; instanceIndex: number; currentLabel: string } | null>(null);
  const [deleteInstance, setDeleteInstance] = useState<{ form: Form; instanceIndex: number; label: string | null } | null>(null);
  // Overflow ("⋮") menu shown on each instance's header. Holds the anchor element plus the
  // instance it belongs to, so a single shared Menu can serve every form panel.
  const [instanceMenu, setInstanceMenu] = useState<{ anchorEl: HTMLElement; form: Form; instanceIndex: number; label: string | null } | null>(null);
  // Post-mutation navigation target. Consumed by an effect once tabSlots reflects the
  // refetched responses — avoids the stale-closure trap of computing the slot index here.
  // instanceIndex undefined means "first surviving slot for this form" (delete fallback).
  const [pendingInstanceNav, setPendingInstanceNav] = useState<{ formId: number; instanceIndex?: number } | null>(null);
  const regenerationQueueRef = useRef<FormPdfFreshness[]>([]);
  const isRegeneratingRef = useRef(false);

  // Prevent PDF regeneration after user operations (download/save/print)
  const skipPdfRegenerationRef = useRef(false);
  const initialPdfGenerationDoneRef = useRef(false);

  // Function to set the regeneration skip flag
  const setSkipPdfRegeneration = useCallback((skip: boolean) => {
    skipPdfRegenerationRef.current = skip;
  }, []);

  // State for salesperson's user data fields (used in forms/PDFs instead of current user's fields)
  const [salespersonUserDataFields, setSalespersonUserDataFields] = useState<UserDataFieldDto[]>([]);
  const [salespersonFieldsLoaded, setSalespersonFieldsLoaded] = useState(false);

  const [formData, setFormData] = useState<UpdateJobDto>({
    id: '',
    customerId: '',
    assignedSalesPersonId: user?.id || '',
    type: JobType.Consultation,
    estimatedValue: 0,
    priority: Priority.Medium
  });

  // Check URL for form slug and set initial tab when forms are loaded.
  // URL: /crm/customers/{customerId}/jobs/{jobId}/forms/{formSlug}[/{instanceIndex}]
  useEffect(() => {
    if (tabSlots.length === 0 || !embedded || !customerId || !id) return;

    const pathname = window.location.pathname;
    const pathParts = pathname.split('/').filter(Boolean);

    if (pathParts.length >= 7 && pathParts[0] === 'crm' && pathParts[1] === 'customers' &&
        pathParts[2] === customerId && pathParts[3] === 'jobs' && pathParts[4] === id &&
        pathParts[5] === 'forms' && pathParts[6]) {
      const formSlug = pathParts[6];
      const requestedInstance = pathParts[7] ? parseInt(pathParts[7], 10) : 1;

      const slotIndex = tabSlots.findIndex(slot => {
        const slug = slot.form.code || slot.form.description.toLowerCase().replace(/\s+/g, '-');
        return slug === formSlug && slot.instanceIndex === (isNaN(requestedInstance) ? 1 : requestedInstance);
      });
      if (slotIndex !== -1) {
        const newTabValue = 3 + slotIndex;
        if (tabValue !== newTabValue) {
          setTabValue(newTabValue);
        }
      }
    }
  }, [tabSlots, embedded, customerId, id]);

  // Debug logging for stale-while-revalidate behavior
  useEffect(() => {
    debugLog('[JobDetailPage] Query state:', {
      job: {
        isLoading: jobLoading,
        isFetching: jobFetching,
        isStale: jobStale,
        hasData: !!job,
      },
      customer: {
        isFetching: customerFetching,
        isStale: customerStale,
        hasData: !!customer,
      },
      appointments: {
        isLoading: loadingAppointments,
        isFetching: appointmentsFetching,
        isStale: appointmentsStale,
        count: appointments.length,
      },
      forms: {
        isLoading: formsLoading,
        count: forms.length,
      },
      jobId: id,
    });
  }, [jobLoading, jobFetching, jobStale, job, customerFetching, customerStale, customer, loadingAppointments, appointmentsFetching, appointmentsStale, appointments, formsLoading, forms, id]);

  // Set error from query error
  useEffect(() => {
    if (jobError) {
      setError(extractApiErrorMessage(jobError, 'Failed to load job'));
    }
  }, [jobError]);

  // Offline status monitoring
  useEffect(() => {
    const handleConnectivityChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const newValue = !customEvent.detail.isOnline;
      setIsOffline(prev => prev === newValue ? prev : newValue);
    };

    // Listen for offline/online events
    window.addEventListener('connectivity-changed', handleConnectivityChange);

    // Also check offline service status periodically to catch any missed updates
    const checkOfflineStatus = () => {
      const isOnline = offlineService.getOnlineStatus();
      setIsOffline(prev => {
        const newValue = !isOnline;
        return prev === newValue ? prev : newValue;
      });
    };

    // Check immediately and then every 2 seconds
    checkOfflineStatus();
    const interval = setInterval(checkOfflineStatus, 2000);

    return () => {
      window.removeEventListener('connectivity-changed', handleConnectivityChange);
      clearInterval(interval);
    };
  }, []);

  // The assigned salesperson's user data fields are embedded on the JobDto by
  // GetJobByIdAsync so any authenticated viewer in the same business can render
  // the salesperson's {{CODE}} placeholders — no separate (admin-gated) call.
  useEffect(() => {
    setSalespersonFieldsLoaded(false);
    if (!job) {
      return;
    }
    if (job.assignedSalesPersonUserDataFields) {
      setSalespersonUserDataFields(job.assignedSalesPersonUserDataFields);
    } else {
      setSalespersonUserDataFields(user?.userDataFields || []);
    }
    setSalespersonFieldsLoaded(true);
  }, [job, user?.userDataFields]);

  // Reset initial PDF generation guard when navigating to a different job
  useEffect(() => {
    initialPdfGenerationDoneRef.current = false;
  }, [id]);

  // Initial PDF generation when job loads or changes (not when form data changes)
  // Note: PDFs are regenerated with fresh parameter values when switching tabs (see handleTabChange)
  // Guard: only run once per job after all data (including customer and salesperson fields) is ready.
  useEffect(() => {
    if (!job || forms.length === 0 || !customer || !salespersonFieldsLoaded || skipPdfRegenerationRef.current) return;
    if (initialPdfGenerationDoneRef.current) return;
    initialPdfGenerationDoneRef.current = true;

    // Generate PDFs only for PixelVersion forms that don't have PDFs yet.
    // For multi-instance forms, generate one PDF per existing instance (one per tab slot).
    tabSlots.forEach(slot => {
      const { form, instanceIndex } = slot;
      if (!isPixelVersionForm(form)) return;
      const key = formKey(form.id, instanceIndex);
      if (loadingPixelVersionPdfs[key] || pixelVersionPdfBytes[key]) return;
      generatePixelVersionPdf(form, false, undefined, instanceIndex);
    });
  }, [forms, tabSlots, job, customer, salespersonFieldsLoaded]);

  // Load upload constraints on mount
  useEffect(() => {
    const loadConstraints = async () => {
      try {
        const constraints = await documentService.getUploadConstraints();
        setUploadConstraints(constraints);
      } catch (err) {
        console.error('Failed to load upload constraints:', err);
      }
    };
    loadConstraints();
  }, []);

  // Load documents on mount so they're ready when user switches to Documents tab
  useEffect(() => {
    if (id) {
      loadAllFormResponses();
    }
  }, [id]);

  // Load form responses when forms are loaded and a form tab is active
  useEffect(() => {
    if (tabSlots.length > 0 && id && tabValue >= 3) {
      const slot = tabSlots[tabValue - 3];
      if (slot) {
        const key = formKey(slot.form.id, slot.instanceIndex);
        if (!formResponses[key] && !loadingFormResponses) {
          loadFormResponses(slot.form.id, true, false, slot.instanceIndex);
        }
      }
    }
  }, [tabSlots, tabValue, id]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleEditClick = async () => {
    if (!job) return;

    // Convert string enum values to numeric enum values for form
    const convertJobType = (type: JobType | string): JobType => {
      if (typeof type === 'string') {
        const stringToEnum: { [key: string]: JobType } = {
          'KitchenDesign': JobType.KitchenDesign,
          'KitchenRemodel': JobType.KitchenRemodel,
          'NewKitchen': JobType.NewKitchen,
          'Consultation': JobType.Consultation,
          'Renovation': JobType.Renovation,
          'Other': JobType.Other
        };
        return stringToEnum[type] || JobType.Consultation;
      }
      return type as JobType;
    };

    const convertPriority = (priority: Priority | string): Priority => {
      if (typeof priority === 'string') {
        const stringToEnum: { [key: string]: Priority } = {
          'Low': Priority.Low,
          'Medium': Priority.Medium,
          'High': Priority.High,
          'Urgent': Priority.Urgent
        };
        return stringToEnum[priority] || Priority.Medium;
      }
      return priority as Priority;
    };

    setFormData({
      id: job.id,
      customerId: job.customerId,
      assignedSalesPersonId: job.assignedSalesPersonId,
      jobAddress: job.jobAddress || '',
      type: convertJobType(job.type),
      estimatedValue: job.estimatedValue,
      actualValue: job.actualValue,
      priority: convertPriority(job.priority),
      expectedCompletionDate: job.expectedCompletionDate,
      notes: job.notes || ''
    });

    setIsEditDialogOpen(true);
  };

  const handleDuplicateClick = () => {
    if (!job) return;
    setDuplicateAddress(job.jobAddress || '');
    setIsDuplicateDialogOpen(true);
  };

  const handleDuplicateJob = async () => {
    if (!job) return;

    setError(null);

    const duplicateDto: DuplicateJobDto = {
      sourceJobId: job.id,
      jobAddress: duplicateAddress || undefined
    };

    duplicateJobMutation.mutate(duplicateDto, {
      onSuccess: (newJob) => {
        setIsDuplicateDialogOpen(false);
        setSuccess('Job duplicated successfully. Opening new job...');

        // Open the new job in a new tab
        const newJobUrl = `${window.location.origin}/crm/jobs/${newJob.id}`;
        window.open(newJobUrl, '_blank');
      },
      onError: (err: any) => {
        setError(extractApiErrorMessage(err, 'Failed to duplicate job'));
      },
    });
  };

  const handleUpdateJob = async () => {
    if (!job) return;

    // Clean up form data - convert empty strings to undefined for optional fields
    const cleanedFormData = {
      ...formData,
      jobAddress: formData.jobAddress || undefined,
      expectedCompletionDate: formData.expectedCompletionDate || undefined,
      notes: formData.notes || undefined
    };

    updateJobMutation.mutate(cleanedFormData, {
      onSuccess: () => {
        setIsEditDialogOpen(false);
        setSuccess('Job updated successfully');
        setError(null);
        refetchJob(); // Refresh job data via TanStack Query
      },
      onError: (err: any) => {
        setError(extractApiErrorMessage(err, 'Failed to update job'));
        setSuccess(null);
      },
    });
  };

  const handleDeleteJob = async () => {
    if (!job || !window.confirm('Are you sure you want to delete this job?')) return;

    deleteJobMutation.mutate(job.id, {
      onSuccess: () => {
        navigate('/crm/jobs');
      },
      onError: (err: any) => {
        setError(extractApiErrorMessage(err, 'Failed to delete job'));
      },
    });
  };

  const handleBack = () => {
    navigate('/crm/jobs');
  };

  // Actually perform the tab change (called directly or after dialog confirmation)
  const performTabChange = (newValue: number) => {
    debugLog('[FORM_ISSUES] performTabChange called, newValue:', newValue);

    setTabValue(newValue);
    setIsFormDirty(false); // Reset dirty state when changing tabs

    // Update URL when embedded
    if (embedded && id) {
      updateUrlForTab(newValue);
    }

    // Always reload all form responses when switching to documents tab to get latest data
    if (newValue === 2 && !loadingDocuments && id) {
      debugLog('[FORM_ISSUES] Switching to documents tab, calling loadAllFormResponses');
      loadAllFormResponses();
      // Also check form PDF freshness and regenerate stale PDFs
      checkFormPdfFreshnessAndRegenerate();
    }

    // Regenerate PDF when switching to a form tab to get latest parameter values
    if (newValue >= 3 && id) {
      const slot = tabSlots[newValue - 3];
      if (slot) {
        const { form, instanceIndex } = slot;
        const key = formKey(form.id, instanceIndex);

        if (isPixelVersionForm(form) && !loadingPixelVersionPdfs[key]) {
          const hasCachedPdf = !!pixelVersionPdfBytes[key];
          generatePixelVersionPdf(form, hasCachedPdf, undefined, instanceIndex);
        }

        if (!isPixelVersionForm(form)) {
          loadFormResponses(form.id, true, true, instanceIndex);
        }
      }
    }
  };

  // performTabChange is recreated each render; hold the latest in a ref so the
  // navigation effect below can call it without listing it as a dependency
  // (which would retrigger the effect on every render).
  const performTabChangeRef = useRef(performTabChange);
  performTabChangeRef.current = performTabChange;

  // Navigate to a form instance once tabSlots reflects the post-mutation refetch.
  // Runs after render, so tabSlots here is the fresh one.
  useEffect(() => {
    if (!pendingInstanceNav) return;

    const { formId, instanceIndex } = pendingInstanceNav;
    const idx = tabSlots.findIndex(s =>
      s.form.id === formId &&
      (instanceIndex === undefined || s.instanceIndex === instanceIndex)
    );

    if (idx >= 0) {
      setPendingInstanceNav(null);
      performTabChangeRef.current(3 + idx);
    } else if (instanceIndex === undefined) {
      // No slot left for this form at all: jump back to Overview.
      setPendingInstanceNav(null);
      setTabValue(0);
    }
    // A specific instance that isn't in tabSlots yet stays pending until the next
    // tabSlots recompute delivers it.
  }, [tabSlots, pendingInstanceNav]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    debugLog('[FORM_ISSUES] handleTabChange called, newValue:', newValue, 'isFormDirty:', isFormDirty);

    // Check if we're on a form tab and have unsaved changes
    if (isFormDirty && tabValue >= 3) {
      const currentSlot = tabSlots[tabValue - 3];
      const currentForm = currentSlot ? currentSlot.form : undefined;
      const isPixelVersion = currentForm && isPixelVersionForm(currentForm);

      // If it's a PixelVersion form with auto-save, allow tab change
      // Auto-save will handle saving the data (either on debounce or the hook will save when component unmounts)
      if (isPixelVersion) {
        debugLog('[JobDetailPage] Form is dirty but auto-save is enabled - allowing tab change');
        // Note: The useAutoSave hook in InteractivePDFViewer will save when debounce completes
        // or immediately if we implement a forceSave on unmount
        performTabChange(newValue);
        return;
      }

      // For non-PixelVersion forms, show the unsaved changes dialog
      debugLog('[JobDetailPage] Blocking tab change - form is dirty');
      setPendingTabChange(newValue);
      setShowUnsavedChangesDialog(true);
      return;
    }

    performTabChange(newValue);
  };

  // Handle dialog confirmation - proceed with pending action
  const handleConfirmLeave = () => {
    debugLog('[JobDetailPage] User confirmed leave - pendingTabChange:', pendingTabChange, 'pendingNavigation:', pendingNavigation);
    setShowUnsavedChangesDialog(false);
    setIsFormDirty(false);

    if (pendingTabChange !== null) {
      performTabChange(pendingTabChange);
      setPendingTabChange(null);
    } else if (pendingNavigation === 'back') {
      // Allow back navigation
      setPendingNavigation(null);
      window.history.back();
    }
  };

  // Handle dialog cancel - stay on current page
  const handleCancelLeave = () => {
    debugLog('[JobDetailPage] User cancelled leave');
    setShowUnsavedChangesDialog(false);
    setPendingTabChange(null);
    setPendingNavigation(null);
  };

  const updateUrlForTab = (tabIndex: number) => {
    if (!id || !customerId) return;

    let newPath = `/crm/customers/${customerId}/jobs/${id}`;

    if (tabIndex === 1) {
      newPath = `/crm/customers/${customerId}/jobs/${id}/communications`;
    } else if (tabIndex === 2) {
      newPath = `/crm/customers/${customerId}/jobs/${id}/documents`;
    } else if (tabIndex >= 3) {
      const slot = tabSlots[tabIndex - 3];
      if (slot) {
        const formSlug = slot.form.code || slot.form.description.toLowerCase().replace(/\s+/g, '-');
        // Stable instance suffix only when > 1 — preserves backward-compatible URLs for 1:1 forms.
        newPath = slot.instanceIndex > 1
          ? `/crm/customers/${customerId}/jobs/${id}/forms/${formSlug}/${slot.instanceIndex}`
          : `/crm/customers/${customerId}/jobs/${id}/forms/${formSlug}`;
      }
    }

    window.history.replaceState(null, '', newPath);
  };

  const loadAllFormResponses = async () => {
    if (!id) return;
    const callId = ++loadDocumentsCallIdRef.current;
    const isLatest = () => callId === loadDocumentsCallIdRef.current;

    setLoadingDocuments(true);
    try {
      const [_, documents] = await Promise.all([
        refetchFormResponses(),
        documentService.getDocumentsForJob(id),
      ]);
      if (!isLatest()) return;
      setUploadedDocuments(documents);
      setLoadingDocuments(false);
    } catch (err: any) {
      if (!isLatest()) return;
      console.error('Failed to load documents:', err);
      setLoadingDocuments(false);
    }
  };

  // Check form PDF freshness and regenerate stale PDFs
  const checkFormPdfFreshnessAndRegenerate = async () => {
    if (!id || !job) return;

    try {
      setCheckingFreshness(true);
      debugLog('[FORM_ISSUES] Checking form PDF freshness...');

      const freshnessResponse = await documentService.getFormPdfFreshness(id);
      setFormPdfFreshness(freshnessResponse.forms);

      // Find stale forms that need regeneration
      const staleForms = freshnessResponse.forms.filter(f => !f.isFresh && f.hasFormResponse);

      if (staleForms.length === 0) {
        debugLog('[FORM_ISSUES] All form PDFs are up to date');
        setCheckingFreshness(false);
        return;
      }

      debugLog('[FORM_ISSUES] Found stale forms:', staleForms.map(f => f.formName));

      // Set up regeneration queue
      // Note: checkingFreshness will be set to false when regeneration queue is empty
      regenerationQueueRef.current = [...staleForms];
      processRegenerationQueue();
    } catch (err: any) {
      console.error('Failed to check form PDF freshness:', err);
      setCheckingFreshness(false);
    }
  };

  // Process the regeneration queue sequentially
  const processRegenerationQueue = async () => {
    if (isRegeneratingRef.current) return;
    if (regenerationQueueRef.current.length === 0) {
      // All done - refresh documents and clear checking state
      setCheckingFreshness(false);
      loadUploadedDocuments(true);
      return;
    }

    isRegeneratingRef.current = true;
    const formFreshness = regenerationQueueRef.current.shift()!;

    const instanceIdx = formFreshness.instanceIndex ?? 1;
    const regenKey = formKey(formFreshness.formId, instanceIdx);

    try {
      debugLog('[FORM_ISSUES] Regenerating PDF for form:', formFreshness.formName, 'instance:', instanceIdx);

      setRegeneratingFormIds(prev => new Set(prev).add(regenKey));

      const form = forms.find(f => f.id === formFreshness.formId);
      if (!form) {
        console.warn('[FORM_ISSUES] Form not found:', formFreshness.formId);
        return;
      }

      const formData = await loadFormResponses(form.id, false, true, instanceIdx) || {};

      const parameterValues = await formService.getFormFieldParameterValues(job!.id, form.id);

      await generateAndStoreFormPdf(
        form,
        job!,
        customer || null,
        appointments,
        formData,
        parameterValues,
        salespersonUserDataFields,
        instanceIdx,
        formFreshness.instanceLabel ?? null
      );

      setFormPdfFreshness(prev =>
        prev.map(f =>
          f.formId === formFreshness.formId && (f.instanceIndex ?? 1) === instanceIdx
            ? { ...f, isFresh: true, hasPdf: true }
            : f
        )
      );

      debugLog('[FORM_ISSUES] PDF regenerated for form:', formFreshness.formName);
    } catch (err: any) {
      console.error('Failed to regenerate PDF for form:', formFreshness.formName, err);
    } finally {
      setRegeneratingFormIds(prev => {
        const next = new Set(prev);
        next.delete(regenKey);
        return next;
      });

      isRegeneratingRef.current = false;

      processRegenerationQueue();
    }
  };

  const loadUploadedDocuments = async (forceRefresh = false) => {
    if (!id) return;

    try {
      const documents = await documentService.getDocumentsForJob(id, forceRefresh);
      setUploadedDocuments(documents);
    } catch (err: any) {
      console.error('Failed to load uploaded documents:', err);
    }
  };

  const loadFormResponses = async (
    formId: number,
    updateState: boolean = true,
    forceRefresh: boolean = false,
    instanceIndex: number = 1
  ): Promise<{ [key: string]: any } | undefined> => {
    debugLog('[FORM_ISSUES] loadFormResponses called:', { formId, instanceIndex, updateState, forceRefresh });
    if (!id) return undefined;

    try {
      let responses = allJobFormResponses;
      const needsRefetch = !responses || responses.length === 0 || forceRefresh;

      if (needsRefetch) {
        if (forceRefresh && updateState) {
          setFormResponses({});
        }
        const result = await refetchFormResponses();
        responses = result.data || [];
      }

      const form = forms.find(f => f.id === formId);
      if (!form) return undefined;

      // Filter responses for this form (any instance; transformJobFormResponsesToFormData picks the right one).
      const formResponsesForForm = responses.filter(response => response.formId === formId);

      const formData = formService.transformJobFormResponsesToFormData(
        formResponsesForForm,
        form,
        instanceIndex
      );

      if (updateState) {
        const key = formKey(formId, instanceIndex);
        setFormResponses(prev => ({
          ...prev,
          [key]: formData,
        }));
      }

      return formData;
    } catch (err: any) {
      console.error('[FORM_ISSUES] Failed to load form responses:', err);
    }

    return undefined;
  };


  // Generate document filename using the same format as FormRenderer
  const generateDocumentFilename = (form: Form): string => {
    const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const sanitizedJobNumber = job?.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
    return `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;
  };

  // Get forms that have been filled out (have responses) and are printable
  const getFormsWithResponses = (): Form[] => {
    if (allJobFormResponses.length === 0) return [];

    // NEW FORMAT: Check if form has a response by formId
    // Find forms that have at least one response and are printable
    const formsWithResponses: Form[] = [];
    forms.forEach(form => {
      // Only include forms that are printable
      if (!form.isPrintable) return;

      const hasResponse = allJobFormResponses.some(response =>
        response.formId === form.id && response.isActive
      );
      
      if (hasResponse) {
        formsWithResponses.push(form);
      }
    });

    // Sort by DisplayOrder
    return formsWithResponses.sort((a, b) => a.displayOrder - b.displayOrder);
  };

  // Helper function to check if form has PixelVersion enabled
  const isPixelVersionForm = (form: Form): boolean => {
    if (!form.pdfConfigurationJson) return false;

    try {
      const pdfConfig = JSON.parse(form.pdfConfigurationJson);
      return pdfConfig.pixelVersion === true;
    } catch (error) {
      console.warn('Failed to parse PDF configuration JSON:', error);
      return false;
    }
  };

  const generatePixelVersionPdf = async (
    form: Form,
    silent: boolean = false,
    overrideFormData?: { [key: string]: any },
    instanceIndex: number = 1
  ) => {
    if (!id || !job) return;

    const key = formKey(form.id, instanceIndex);

    try {
      if (!silent) {
        setLoadingPixelVersionPdfs(prev => ({ ...prev, [key]: true }));
      }
      setError(null);

      let mergedFormData: { [key: string]: any };
      let formParameterValues: { [fieldId: number]: string } = {};

      if (overrideFormData) {
        formParameterValues = await formService.getFormFieldParameterValues(id, form.id);
        mergedFormData = overrideFormData;
      } else {
        const [formData, fetchedParameterValues] = await Promise.all([
          loadFormResponses(form.id, false, true, instanceIndex),
          formService.getFormFieldParameterValues(id, form.id)
        ]);
        formParameterValues = fetchedParameterValues;
        mergedFormData = { ...(formParameterValues || {}), ...(formData || {}) };
      }

      const pdfBytes = await generateFormPdf(
        form,
        job,
        customer,
        appointments,
        mergedFormData,
        formParameterValues,
        salespersonUserDataFields,
        { isInteractiveView: true }
      );

      setFormResponses(prev => ({ ...prev, [key]: mergedFormData }));
      setPixelVersionPdfBytes(prev => ({ ...prev, [key]: pdfBytes }));

      if (!silent) {
        setLoadingPixelVersionPdfs(prev => ({ ...prev, [key]: false }));
      }
    } catch (err: any) {
      console.error('Error generating PixelVersion PDF:', err);

      if (!silent) {
        setError(extractApiErrorMessage(err, 'Failed to generate PDF'));
        setLoadingPixelVersionPdfs(prev => ({ ...prev, [key]: false }));
      }
    }
  };

  // Handle applying parameter changes from source forms
  const handleApplyParameterChanges = async (form: Form, updates: { [fieldCode: string]: any }, instanceIndex: number = 1) => {
    debugLog('[JobDetailPage] Applying parameter changes for form:', form.id, 'instance:', instanceIndex, 'updates:', updates);

    // For CheckboxList field updates, also set the matching individual item entries
    // (e.g. INSTALL=["YES"] -> INSTALL_YES=true, INSTALL_NO=false). The PDF renderer
    // and the save-time cleanup both work off the per-item state, so the parent value
    // alone won't visually update or survive a save without these.
    const expandedUpdates: { [key: string]: any } = { ...updates };
    if (form.formSections) {
      for (const section of form.formSections) {
        for (const field of section.formFields || []) {
          const isCheckboxList = typeof field.fieldType === 'string'
            ? field.fieldType === 'CheckboxList'
            : field.fieldType === FormFieldType.CheckboxList;
          if (!isCheckboxList) continue;
          if (!(field.code in updates)) continue;
          const newValue = updates[field.code];
          const selected = Array.isArray(newValue) ? newValue : [];
          const items = field.formFieldDataType?.formFieldDataItems || [];
          for (const item of items) {
            expandedUpdates[`${field.code}_${item.code}`] = selected.includes(item.code);
          }
        }
      }
    }

    const key = formKey(form.id, instanceIndex);
    const currentFormData = formResponses[key] || {};
    const updatedFormData = {
      ...currentFormData,
      ...expandedUpdates
    };

    await generatePixelVersionPdf(form, true, updatedFormData, instanceIndex);

    if (id) {
      try {
        await handleSaveFormDataOnly(form, id, updatedFormData, loadFormResponses, instanceIndex);
      } catch (err) {
        console.error('[JobDetailPage] Auto-save after parameter change apply failed:', err);
      }
    }
  };

  const handleRemoveDocument = useCallback((documentId: string) => {
    setUploadedDocuments(prev => prev.filter(d => d.id !== documentId));
  }, []);

  // Multi-instance form mutations: create, rename, delete.
  // After each mutation we refetch the JobFormResponses list (the source of truth
  // that drives tabSlots) and then navigate to the appropriate tab.
  const handleConfirmAddInstance = async () => {
    if (!addInstanceForm || !id) return;
    try {
      const trimmed = addInstanceLabel.trim();
      const newResponse = await formService.createFormInstance(id, addInstanceForm.id, trimmed.length > 0 ? trimmed : null);
      await refetchFormResponses();

      setAddInstanceForm(null);
      setAddInstanceLabel('');
      // Navigation happens in the pendingInstanceNav effect once tabSlots includes the new slot.
      setPendingInstanceNav({ formId: addInstanceForm.id, instanceIndex: newResponse.instanceIndex });
      setSuccess('Form copy added');
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Failed to create form instance'));
    }
  };

  // Open the "add a copy" dialog. Stable so the memoized PDF viewer toolbar doesn't churn.
  const handleNewFormInstance = useCallback((form: Form) => {
    setAddInstanceForm(form);
    setAddInstanceLabel('');
  }, []);

  // Instance overflow menu: open from the kebab, then route to the rename/delete dialogs.
  const handleOpenInstanceMenu = useCallback(
    (anchorEl: HTMLElement, form: Form, instanceIndex: number, label: string | null) => {
      setInstanceMenu({ anchorEl, form, instanceIndex, label });
    },
    []
  );
  const handleCloseInstanceMenu = () => setInstanceMenu(null);

  const handleRenameFromMenu = () => {
    if (instanceMenu) {
      setRenameInstance({ form: instanceMenu.form, instanceIndex: instanceMenu.instanceIndex, currentLabel: instanceMenu.label ?? '' });
    }
    handleCloseInstanceMenu();
  };

  const handleDeleteFromMenu = () => {
    if (instanceMenu) {
      setDeleteInstance({ form: instanceMenu.form, instanceIndex: instanceMenu.instanceIndex, label: instanceMenu.label });
    }
    handleCloseInstanceMenu();
  };

  const handleConfirmRename = async () => {
    if (!renameInstance || !id) return;
    try {
      const trimmed = renameInstance.currentLabel.trim();
      await formService.renameFormInstance(id, renameInstance.form.id, renameInstance.instanceIndex, trimmed.length > 0 ? trimmed : null);
      await refetchFormResponses();
      setRenameInstance(null);
      setSuccess('Label updated');
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Failed to rename instance'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteInstance || !id) return;
    try {
      await formService.archiveFormInstance(id, deleteInstance.form.id, deleteInstance.instanceIndex);
      await refetchFormResponses();
      setDeleteInstance(null);
      // Snap to the form's first surviving slot (the implied #1 when none remain);
      // handled by the pendingInstanceNav effect so it sees the refreshed tabSlots.
      setPendingInstanceNav({ formId: deleteInstance.form.id });
      setSuccess('Form copy deleted');
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Failed to delete instance'));
    }
  };

  const handlePrintDocument = async (form: Form) => {
    if (!id || !job) return;

    try {
      setError(null);

      // Always reload form responses to ensure we have the latest data
      const [formData, formParameterValues] = await Promise.all([
        loadFormResponses(form.id),
        formService.getFormFieldParameterValues(id, form.id)
      ]);
      const initialFormData = formData || {};

      debugLog('Printing document for form:', form.description);
      debugLog('Form data loaded:', initialFormData);
      debugLog('Form data keys:', Object.keys(initialFormData));
      debugLog('Form data has values:', Object.keys(initialFormData).length > 0);

      const pdfBytes = await generateFormPdf(
        form,
        job,
        customer,
        appointments,
        initialFormData,
        formParameterValues,
        salespersonUserDataFields
      );

      // Create blob and open in new tab
      const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Open PDF in new tab - user can print from there
      window.open(url, '_blank');

      // Clean up URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);
    } catch (err: any) {
      console.error('Error printing document:', err);
      setError(extractApiErrorMessage(err, 'Failed to generate PDF for printing'));
    }
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    const appointmentDate = new Date(appointment.startTime);
    navigate('/calendar', {
      state: {
        date: appointmentDate.toISOString(),
        appointmentId: appointment.id
      }
    });
  };

  if (error && !job && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  if (!loading && !job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Job not found
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  // Show loading skeleton when job is still being fetched
  if (loading && !job) {
    return (
      <Box>
        {/* Tabs skeleton */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3, borderRadius: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
            <Skeleton variant="rectangular" width={120} height={48} />
            <Skeleton variant="rectangular" width={110} height={48} />
            <Skeleton variant="rectangular" width={140} height={48} />
            <Skeleton variant="rectangular" width={130} height={48} />
          </Box>
        </Paper>

        {/* Content skeleton */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            <Card sx={{ backgroundColor: theme.accentColor }}>
              <CardContent>
                <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
                <Divider sx={{ mb: 2 }} />
                {Array.from({ length: 8 }).map((_, index) => (
                  <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                    <Box flex={1}>
                      <Skeleton variant="text" width={100} height={16} />
                      <Skeleton variant="text" width={200} height={24} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          {!embedded && (
            <Grid item xs={12} lg={8}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
                  <Divider sx={{ mb: 2 }} />
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                      <Box flex={1}>
                        <Skeleton variant="text" width={100} height={16} />
                        <Skeleton variant="text" width={220} height={24} />
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }

  // Type guard: if we've passed all checks, job should exist
  if (!job) {
    return null;
  }

  // Build the title string
  const getPageTitle = () => {
    const parts: string[] = [];
    if (job.jobAddress) {
      // Get just the first part of the address (before first comma or newline)
      const firstPart = job.jobAddress.split(',')[0].split('\n')[0].trim();
      parts.push(firstPart);
    }
    if (parts.length === 0) {
      return job.jobNumber;
    }
    return parts.join(' - ');
  };

  const pageTitle = getPageTitle();

  return (
    <>
      <Helmet>
        <title>Job: {pageTitle} | MindShare Calendar</title>
      </Helmet>

      <Box>
        {/* Header - Hidden when embedded */}
        {!embedded && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={handleBack}
              color="primary"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              {loading || !job ? (
                <>
                  <Skeleton variant="text" width={250} height={40} />
                </>
              ) : (
                <Typography variant="h5" component="h5">
                  {pageTitle}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            {loading ? (
              <>
                <Skeleton variant="rectangular" width={100} height={36} />
                <Skeleton variant="rectangular" width={120} height={36} />
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEditClick}
                  disabled={isOffline}
                >
                  Edit Job
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DuplicateIcon />}
                  onClick={handleDuplicateClick}
                  disabled={isOffline}
                >
                  Duplicate
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteJob}
                  disabled={isOffline}
                  sx={{ display: 'none' }}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3, borderRadius: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
              <Skeleton variant="rectangular" width={120} height={48} />
              <Skeleton variant="rectangular" width={110} height={48} />
              <Skeleton variant="rectangular" width={140} height={48} />
              <Skeleton variant="rectangular" width={130} height={48} />
            </Box>
          ) : (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="job detail tabs"
              data-testid="job-detail-tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Job Overview" data-testid="job-tab-overview" />
              <Tab label="Communications" data-testid="job-tab-communications" />
              <Tab label="Documents" data-testid="job-tab-documents" />
              {loadingForms ? (
                // Show skeleton tabs while forms are loading
                <>
                  {[1, 2, 3].map((index) => (
                    <Tab
                      key={`skeleton-${index}`}
                      disabled
                      label={
                        <Skeleton
                          variant="text"
                          width={80}
                          height={20}
                          sx={{ display: 'inline-block' }}
                        />
                      }
                    />
                  ))}
                </>
              ) : (
                tabSlots.map((slot, index) => {
                  const suffix = slot.label
                    ? ` – ${slot.label}`
                    : (slot.instanceIndex > 1 ? ` #${slot.instanceIndex}` : '');
                  return (
                    <Tab
                      key={`${slot.form.id}:${slot.instanceIndex}`}
                      label={`${slot.form.description}${suffix}`}
                      data-testid={`job-tab-form-${slot.form.code}${slot.instanceIndex > 1 ? `-${slot.instanceIndex}` : ''}`}
                    />
                  );
                })
              )}
            </Tabs>
          )}
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Row 1: Job Details, Customer Details, Appointments */}
            {/* Job Details - Left column */}
            <Grid item xs={12} lg={4}>
              {loading ? (
                <Card sx={{ backgroundColor: theme.accentColor }}>
                  <CardContent>
                    <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
                    <Divider sx={{ mb: 2 }} />
                    {Array.from({ length: 8 }).map((_, index) => (
                      <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                        <Box flex={1}>
                          <Skeleton variant="text" width={100} height={16} />
                          <Skeleton variant="text" width={200} height={24} />
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              ) : (
                <JobDetailsCard job={job} theme={theme} />
              )}
            </Grid>

            {/* Customer Details - right column (only on large screens) */}
            {!embedded && (
              <Grid item xs={12} lg={8}>
                <Card sx={{ backgroundColor: theme.accentColor }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Customer Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {loading ? (
                      <>
                        {Array.from({ length: 5 }).map((_, index) => (
                          <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                            <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                            <Box flex={1}>
                              <Skeleton variant="text" width={100} height={16} />
                              <Skeleton variant="text" width={220} height={24} />
                            </Box>
                          </Box>
                        ))}
                      </>
                    ) : customer ? (
                      <CustomerDetailsCard customer={customer} />
                    ) : (
                      <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Appointments - Right column */}
            <Grid item xs={12} lg={8}>
              <AppointmentsList
                appointments={appointments}
                appointmentTypes={appointmentTypes}
                loadingAppointments={loadingAppointments}
                theme={theme}
                onAppointmentClick={handleAppointmentClick}
              />
            </Grid>


            {/* Communication directly under Appointments (8 columns) 
            <Grid item xs={12} lg={12}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  <Typography variant="h6">Communication</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Communication logs for this job will be displayed here.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>*/}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card sx={{ backgroundColor: theme.accentColor }}>
            <CardContent>
              <CommunicationLogForm
                jobId={id || ''}
                customerId={job?.customerId}
                onSubmit={async (data: CreateCommunicationLogDto) => {
                  await createCommunicationLogMutation.mutateAsync(data);
                  setSuccess('Communication log added successfully');
                }}
                isSubmitting={createCommunicationLogMutation.isPending}
              />
              <Typography variant="h6" gutterBottom>Communication History</Typography>
              <CommunicationLogsList
                logs={communicationLogsData || []}
                loading={communicationLogsLoading}
                emptyMessage="No communication logs found for this job."
              />
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <JobDocumentsTab
            jobId={id || ''}
            uploadedDocuments={uploadedDocuments}
            loadingDocuments={loadingDocuments}
            isOffline={isOffline}
            uploadConstraints={uploadConstraints}
            onReloadDocuments={loadUploadedDocuments}
            onRemoveDocument={handleRemoveDocument}
            onError={setError}
            onSuccess={setSuccess}
            staleForms={formPdfFreshness}
            regeneratingFormIds={regeneratingFormIds}
            checkingFreshness={checkingFreshness}
          />
        </TabPanel>

        {/* Dynamic Form Tabs — one panel per slot. */}
        {tabSlots.map((slot, index) => {
          const { form, instanceIndex, label } = slot;
          const tabIndex = 3 + index;
          const isPixelVersion = isPixelVersionForm(form);
          const key = formKey(form.id, instanceIndex);

          // The per-instance menu (rename/delete) only applies once a real instance row exists —
          // the implied #1 slot (a fresh form with no saved rows) has nothing to act on yet.
          // The "+ New Form" button and this menu render inline on the PDF viewer's toolbar.
          //
          // INVARIANT: the multi-instance UX (+ New Form / rename / delete) is wired only into the
          // PixelVersionPDFViewer below — so multi-instance is currently supported for PixelVersion
          // forms only. A non-PixelVersion form flagged allowMultipleInstances would show its implied
          // #1 tab but expose no way to add copies; keep multi-instance forms on PixelVersion.
          const instanceExists = allJobFormResponses.some(
            r => r.formId === form.id && (r.instanceIndex ?? 1) === instanceIndex && r.isActive
          );

          return (
            <TabPanel key={key} value={tabValue} index={tabIndex}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  {loadingForms ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress />
                    </Box>
                  ) : isPixelVersion ? (
                    <PixelVersionPDFViewer
                      pdfBytes={pixelVersionPdfBytes[key] || null}
                      loading={loadingPixelVersionPdfs[key] || false}
                      form={form}
                      job={job}
                      customer={customer}
                      appointments={appointments}
                      loadFormResponses={(formId: number, updateState?: boolean, forceRefresh?: boolean) =>
                        loadFormResponses(formId, updateState, forceRefresh, instanceIndex)
                      }
                      generatePixelVersionPdf={(formArg: Form, silent?: boolean, overrideFormData?: any) =>
                        generatePixelVersionPdf(formArg, silent ?? false, overrideFormData, instanceIndex)
                      }
                      setSuccess={setSuccess}
                      setError={setError}
                      setSkipPdfRegeneration={setSkipPdfRegeneration}
                      userDataFields={salespersonUserDataFields}
                      initialFormData={formResponses[key]}
                      onDocumentSaved={() => loadUploadedDocuments(false)}
                      onApplyParameterChanges={(updates) => handleApplyParameterChanges(form, updates, instanceIndex)}
                      onDirtyChange={setIsFormDirty}
                      instanceIndex={instanceIndex}
                      instanceLabel={label}
                      instanceExists={instanceExists}
                      onNewFormInstance={handleNewFormInstance}
                      onOpenInstanceMenu={handleOpenInstanceMenu}
                    />
                  ) : (
                    <FormRenderer
                      form={form}
                      jobId={id}
                      jobNumber={job.jobNumber}
                      customerId={customer?.id}
                      assignedSalesPersonName={job.assignedSalesPersonName}
                      job={{ ...job, appointments }}
                      customer={customer}
                      instanceIndex={instanceIndex}
                      instanceLabel={label}
                      existingResponse={formResponses[key]}
                      isLoadingResponses={loadingFormResponses}
                      onSubmit={(responseData) => {
                        setSuccess('Form submitted successfully');
                        setIsFormDirty(false);
                        if (id) {
                          loadFormResponses(form.id, true, true, instanceIndex);
                        }
                      }}
                      onDirtyChange={setIsFormDirty}
                    />
                  )}
                </CardContent>
              </Card>
            </TabPanel>
          );
        })}

        {/* Edit Job Dialog */}
        <BaseDialog
          open={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          title="Edit Job"
          fullWidth
          maxWidth="md"
          contentSx={{ pb: 4 }}
          actions={
            <>
              <Button
                onClick={() => setIsEditDialogOpen(false)}
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateJob} variant="contained">Update</Button>
            </>
          }
        >
          {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Grid container spacing={2}>
            {customer && (
              <Grid item xs={12}>
                <CustomerDetailsCard customer={customer} />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Assigned Sales Person</InputLabel>
                <Select
                  value={formData.assignedSalesPersonId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, assignedSalesPersonId: e.target.value }))}
                  label="Assigned Sales Person"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {businessUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <JobForm
              formData={formData}
              onFormDataChange={(data) => setFormData({ ...formData, ...data })}
              defaultBounds={{
                city: business?.defaultCity,
                country: business?.defaultCountry
              }}
              showCustomerSelection={false}
            />
          </Grid>
        </BaseDialog>

        {/* Duplicate Job Dialog */}
        <DuplicateJobDialog
          open={isDuplicateDialogOpen}
          onClose={() => setIsDuplicateDialogOpen(false)}
          onDuplicate={handleDuplicateJob}
          duplicateAddress={duplicateAddress}
          onAddressChange={setDuplicateAddress}
          isDuplicating={duplicateJobMutation.isPending}
        />

        {/* Add Form Instance Dialog */}
        <BaseDialog
          open={!!addInstanceForm}
          onClose={() => { setAddInstanceForm(null); setAddInstanceLabel(''); }}
          title={addInstanceForm ? `Add ${addInstanceForm.description}` : 'Add'}
          fullWidth
          maxWidth="xs"
          actions={
            <>
              <Button onClick={() => { setAddInstanceForm(null); setAddInstanceLabel(''); }} variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleConfirmAddInstance} variant="contained" data-testid="form-instance-add-confirm">
                Add
              </Button>
            </>
          }
        >
          <Typography variant="body2" sx={{ mb: 2 }}>
            Give this copy a label so you can tell it apart from the others — e.g. "Kitchen" or "Laundry".
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Label"
            value={addInstanceLabel}
            onChange={(e) => setAddInstanceLabel(e.target.value)}
            placeholder="e.g. Kitchen"
            inputProps={{ maxLength: 100, 'data-testid': 'form-instance-add-label' }}
          />
        </BaseDialog>

        {/* Per-instance overflow menu (shared across all form panels) */}
        <Menu
          anchorEl={instanceMenu?.anchorEl ?? null}
          open={!!instanceMenu}
          onClose={handleCloseInstanceMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={handleRenameFromMenu}
            data-testid={instanceMenu ? `form-instance-rename-${instanceMenu.form.code}-${instanceMenu.instanceIndex}` : undefined}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleDeleteFromMenu}
            sx={{ color: 'error.main' }}
            data-testid={instanceMenu ? `form-instance-delete-${instanceMenu.form.code}-${instanceMenu.instanceIndex}` : undefined}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Rename Form Instance Dialog */}
        <BaseDialog
          open={!!renameInstance}
          onClose={() => setRenameInstance(null)}
          title={renameInstance ? `Rename ${renameInstance.form.description} #${renameInstance.instanceIndex}` : 'Rename'}
          fullWidth
          maxWidth="xs"
          actions={
            <>
              <Button onClick={() => setRenameInstance(null)} variant="outlined">Cancel</Button>
              <Button onClick={handleConfirmRename} variant="contained" data-testid="form-instance-rename-confirm">
                Save
              </Button>
            </>
          }
        >
          <TextField
            autoFocus
            fullWidth
            label="Label"
            value={renameInstance?.currentLabel ?? ''}
            onChange={(e) => setRenameInstance(prev => prev ? { ...prev, currentLabel: e.target.value } : prev)}
            inputProps={{ maxLength: 100, 'data-testid': 'form-instance-rename-label' }}
          />
        </BaseDialog>

        {/* Delete Form Instance Confirmation */}
        <BaseDialog
          open={!!deleteInstance}
          onClose={() => setDeleteInstance(null)}
          title="Delete form?"
          fullWidth
          maxWidth="xs"
          actions={
            <>
              <Button onClick={() => setDeleteInstance(null)} variant="outlined">Cancel</Button>
              <Button onClick={handleConfirmDelete} variant="contained" color="error" data-testid="form-instance-delete-confirm">
                Delete
              </Button>
            </>
          }
        >
          <Typography>
            {deleteInstance && (
              <>
                Delete <strong>{deleteInstance.form.description}{deleteInstance.label ? ` – ${deleteInstance.label}` : ` #${deleteInstance.instanceIndex}`}</strong>?
                Its data cannot be recovered.
              </>
            )}
          </Typography>
        </BaseDialog>

        {/* Unsaved Changes Dialog */}
        <UnsavedChangesDialog
          open={showUnsavedChangesDialog}
          onStay={handleCancelLeave}
          onLeave={handleConfirmLeave}
        />

      </Box>
    </>
  );
};

export default JobDetailPage;
